/** Admin - the company Admin's place (KD, 2026-11-01; ADR-0212): eight tabs - People and roles,
 * Approval routes, Security, Integrations, Company, Data, Billing and usage, and the Admin log.
 *
 * **The Admin's alone.** `routeTable.ts` offers the place to the Admin and the server refuses every
 * Admin read to anyone else (`MAY_ADMINISTER`), so a person who types the address is told whose
 * decision it is and shown only the panels that decide for themselves who sees what; no form that
 * would 403.
 *
 * **Each tab is a page of its own.** People and roles is a grid (`People.tsx`); Billing and usage and
 * the Admin log are the existing screens' content in this frame (`BillingContent`, `AuditContent`),
 * so their addresses and their tests stand; the other five are the served sections of
 * `GET /administration` (`adminTabs.ts` says which sit where), each a card with its state and its
 * statement as the endpoint wrote it. Policies are read here, not edited, and a section that is not
 * built says what stands in the way. The tallies over the served states are the four tiles, drawn
 * only once the read succeeded; a failed read is a failed read with a retry.
 *
 * What stands elsewhere and is reached from here: Financials and scope (`/financials`, from Company)
 * and the AI assistant (`/settings/ai`, from Integrations) keep their screens and light the tab they
 * belong to (`routeTable.ts`, `under`).
 */

import {
  Buildings,
  ClockCounterClockwise,
  Gear,
  Plugs,
  Receipt,
  ShieldCheck,
  ShieldStar,
  Database,
  Signature,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useMe } from "../api/auth";
import { useAdministration } from "../api/queries";
import { ReadFailed } from "../components/Loaded";
import { adminStateWords } from "../lib/customerWords";
import { Panel } from "../components/Panel";
import { TablePage, Tile, TileStrip } from "../components/ui";
import { RequireSession } from "../shell/RequireSession";
import { LicencePanel } from "./Administration";
import { ADMIN_TABS, SECTION_TABS, adminTabOf } from "./adminTabs";
import type { AdminTabKey } from "./adminTabs";
import own from "./AdminSections.module.css";
import { Section, tallies } from "./AdminSections";
import { ApprovalsRoutePanel } from "./ApprovalsRoutes";
import { AuditContent } from "./Audit";
import { BillingContent } from "./Billing";
import { LivenessPanel } from "./LivenessPanel";
import { PeopleBody } from "./People";
import { ScreenFrame } from "./ScreenFrame";
import { SystemDisclosure } from "./SystemDisclosure";
import { keepSearch } from "./search";
import styles from "./Screen.module.css";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";

const ICON: Record<AdminTabKey, Icon> = {
  people: Gear,
  routes: Signature,
  security: ShieldCheck,
  integrations: Plugs,
  company: Buildings,
  data: Database,
  billing: Receipt,
  log: ClockCounterClockwise,
};

/** A tab that is a panel or a list in the Admin place's frame: the header card, and the content in
 * the table card's place. */
function AdminPage({
  tab,
  actions,
  tiles,
  children,
}: {
  tab: AdminTabKey;
  actions?: ReactNode;
  tiles?: ReactNode;
  children: ReactNode;
}) {
  const label = ADMIN_TABS.find((entry) => entry.key === tab)?.label ?? "";
  return (
    <TablePage
      icon={ICON[tab]}
      title="Admin"
      label={label}
      actions={actions}
      tiles={tiles}
      state={<div className={own.page}>{children}</div>}
    />
  );
}

/** The cards of the sections that sit on a tab, or the sentence that says none are served for it. */
function SectionCards({
  administration,
  tab,
}: {
  administration: ReturnType<typeof useAdministration>;
  tab: AdminTabKey;
}) {
  if (administration.error) {
    return (
      <ReadFailed
        of="This workspace's settings"
        error={administration.error}
        onRetry={() => void administration.refetch()}
      />
    );
  }
  if (!administration.data) {
    return (
      <p role="status" aria-busy="true">
        Loading…
      </p>
    );
  }
  const sections = administration.data.sections.filter((section) => adminTabOf(section.key) === tab);
  const label = ADMIN_TABS.find((entry) => entry.key === tab)?.label;
  return (
    <Panel title={administration.data.organization} sub={label} column="1 / 13">
      {sections.length ? (
        <table className={own.sections} data-testid="administration" data-tab={tab} aria-label={`${label ?? "Admin"} settings`}>
          {sections.map((section) => (
            <Section key={section.key} section={section} />
          ))}
        </table>
      ) : (
        <p className={styles.note} data-testid="administration-tab-empty">
          Nothing is served for this tab.
        </p>
      )}
    </Panel>
  );
}

function AdminTabs({ screen }: { screen: ReturnType<typeof useScreen> }) {
  const [tab] = useUrlTab(ADMIN_TABS);
  const administration = useAdministration(SECTION_TABS.includes(tab));
  const count = tallies(administration, (key) => adminTabOf(key) === tab);
  const show = (n: number | undefined) => (n === undefined ? "-" : String(n));

  // **Tallies of what the endpoint served, and nothing the screen believes** - on the tabs that are a
  // list of served sections, and only from a read that succeeded (a dash, never four zeros).
  const tiles = SECTION_TABS.includes(tab) ? (
    <TileStrip label="Admin at a glance">
      <Tile value={show(count.inUse)} label={adminStateWords("settled")} testId="admin-in-use" />
      <Tile value={show(count.inForce)} label={adminStateWords("read_only")} testId="admin-in-force" />
      <Tile value={show(count.nothingYet)} label={adminStateWords("none_yet")} testId="admin-nothing-yet" />
      <Tile value={show(count.notBuilt)} label={adminStateWords("not_built")} testId="admin-not-built" />
    </TileStrip>
  ) : undefined;

  let body: ReactNode;
  switch (tab) {
    case "people":
      body = <PeopleBody />;
      break;
    case "routes":
      body = (
        <AdminPage tab={tab} tiles={tiles}>
          <ApprovalsRoutePanel />
          <SectionCards administration={administration} tab={tab} />
        </AdminPage>
      );
      break;
    case "security":
      body = (
        <AdminPage tab={tab} tiles={tiles}>
          <SectionCards administration={administration} tab={tab} />
        </AdminPage>
      );
      break;
    case "integrations":
      body = (
        <AdminPage
          tab={tab}
          tiles={tiles}
          actions={
            <Link to="/settings/ai" search={keepSearch(screen)} data-testid="admin-open-assistant">
              AI assistant
            </Link>
          }
        >
          <SectionCards administration={administration} tab={tab} />
          {/* The panel is its own, so it sits behind a disclosure (one table to a tab): opened, it is
              the same panel the Approver reads inline in the branch below. */}
          <SystemDisclosure summary="Unattended work: has the scheduled work run" testId="liveness-disclosure" lazy>
            <LivenessPanel />
          </SystemDisclosure>
        </AdminPage>
      );
      break;
    case "company":
      body = (
        <AdminPage
          tab={tab}
          tiles={tiles}
          actions={
            <Link to="/financials" search={keepSearch(screen)} data-testid="admin-open-financials">
              Financials and scope
            </Link>
          }
        >
          <SectionCards administration={administration} tab={tab} />
          <LicencePanel />
        </AdminPage>
      );
      break;
    case "data":
      body = (
        <AdminPage tab={tab} tiles={tiles}>
          <SectionCards administration={administration} tab={tab} />
        </AdminPage>
      );
      break;
    case "billing":
      body = (
        <AdminPage tab={tab}>
          <BillingContent />
        </AdminPage>
      );
      break;
    case "log":
      body = (
        <AdminPage tab={tab}>
          <AuditContent />
        </AdminPage>
      );
      break;
  }
  return body;
}

function AdminBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";

  if (!isAdmin) {
    // One sentence, in the same page frame as every other place (a header card, and the sentence in the
    // table card's place). It used to be the old operations screen: three cards of unequal width, an
    // approval-routes panel and a licence panel that each said "ask an Admin". Whose decision it is is the
    // whole answer; the scheduled work's check an Approver may read is on Support ("Service checks").
    return (
      <ScreenFrame screen={screen} title="Admin">
        <TablePage
          icon={ShieldStar}
          title="Admin"
          state={
            <p className={styles.note} data-testid="admin-only">
              An <strong>Admin</strong> sees how this workspace is set up and{" "}
              <span data-testid="licence-who-can">
                how many people, companies and sites it has
              </span>
              .
            </p>
          }
        />
      </ScreenFrame>
    );
  }
  return (
    <ScreenFrame screen={screen} title="Admin">
      <AdminTabs screen={screen} />
    </ScreenFrame>
  );
}

export function AdminScreen() {
  return (
    <RequireSession>
      <AdminBody />
    </RequireSession>
  );
}
